import { Add, Delete } from '@mui/icons-material';
import { Box, Typography, Button, IconButton, Paper, Divider, Tabs, Tab } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { useMemo, useState, type SyntheticEvent } from 'react';
import { useSelector } from 'react-redux';

import FormSkeleton from './formSkeleton';
import StepNote from './stepNote';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { FormikRangeSlider } from 'components/common/formikForm/FormikRangeSlider';
import { StepNavigation } from 'components/v2/StepNavigation';

import { changeTabIfValid } from './tabValidation';
import {
    BooleanValue,
    EnglishProficiencyTests,
    FrenchProficiencyTests,
    partnerLanguages,
} from 'constant';
import useEnabledCountries from 'hooks/useEnabledCountries';
import languages from 'data/selectInputOptions/languageList.json';
import {
    useLanguageProficiency,
    useSpouseLanguageProficiency,
    useSaveLanguageProficiency,
    useSaveSpouseLanguageProficiency,
} from 'hooks';
import { RootState } from 'redux/store';
import type { LanguageProficiency } from 'types';
import { generateOptionFromObject, generateOptionsFromString } from 'utils/dataFormatter';
import {
    formatLanguageScoreLabel,
    getLanguageScoreRange,
    getLanguageScoreStep,
} from 'utils/v2/visaApplication';
import { languageSchema } from 'utils/validations/languageSchema';

const languageOptions = generateOptionsFromString(
    Object.values(languages).map((item) => item.name)
);
const englishTestOptions = generateOptionsFromString(Object.values(EnglishProficiencyTests));
const frenchTestOptions = generateOptionsFromString(Object.values(FrenchProficiencyTests));
const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);

const NATIVE_LEVEL = 5;

const createEmptyLanguage = (userId = 0): LanguageProficiency => ({
    language: '',
    level: null,
    listening: 0,
    reading: 0,
    speaking: 0,
    spouse_id: null,
    test_date: '',
    test_type: '',
    user_id: userId,
    writing: 0,
});

/** Native language row — auto-filled scores match LanguageFormV2 initNative. */
const createNativeLanguage = (language: string, userId = 0): LanguageProficiency => ({
    ...createEmptyLanguage(userId),
    language,
    level: NATIVE_LEVEL,
    listening: 9,
    reading: 9,
    speaking: 9,
    writing: 9,
    test_type: EnglishProficiencyTests.SELF_ASSESSMENT,
});

const createProficiencyLanguage = (
    language: 'English' | 'French',
    userId = 0
): LanguageProficiency => ({
    ...createEmptyLanguage(userId),
    language,
    level: null,
});

/**
 * Rebuild the user_languages array when the native language changes.
 * Mirrors LanguageFormV2 handleMotherLanguage.
 */
const buildLanguagesForNative = (
    nativeLanguage: string,
    previous: LanguageProficiency[],
    userId: number,
    keepFrench: boolean
): LanguageProficiency[] => {
    const existingEnglish = previous.find(
        (lang) => lang.language === 'English' && lang.level !== NATIVE_LEVEL
    );
    const existingFrench = previous.find(
        (lang) => lang.language === 'French' && lang.level !== NATIVE_LEVEL
    );

    if (nativeLanguage === 'English') {
        const next: LanguageProficiency[] = [createNativeLanguage('English', userId)];
        if (keepFrench || existingFrench) {
            next.push(existingFrench ?? createProficiencyLanguage('French', userId));
        }
        return next;
    }

    if (nativeLanguage === 'French') {
        return [
            createNativeLanguage('French', userId),
            existingEnglish ?? createProficiencyLanguage('English', userId),
        ];
    }

    const next: LanguageProficiency[] = [
        createNativeLanguage(nativeLanguage, userId),
        existingEnglish ?? createProficiencyLanguage('English', userId),
    ];
    if (keepFrench || existingFrench) {
        next.push(existingFrench ?? createProficiencyLanguage('French', userId));
    }
    return next;
};

/** Normalize API data into the same shape LanguageFormV2 loadData produced. */
const normalizeUserLanguages = (
    data: LanguageProficiency[] | undefined,
    userId: number
): LanguageProficiency[] => {
    if (!data || data.length === 0) {
        return [createNativeLanguage('', userId)];
    }

    const native = data.find((x) => Number(x.level) === NATIVE_LEVEL) ?? data[0];
    const nativeName = native.language || '';
    const english = data.find((x) => x.language === 'English' && x !== native);
    const french = data.find((x) => x.language === 'French' && x !== native);

    if (nativeName === 'English') {
        const next: LanguageProficiency[] = [
            {
                ...createNativeLanguage('English', userId),
                ...native,
                language: 'English',
                level: NATIVE_LEVEL,
            },
        ];
        if (french) {
            next.push({ ...french, level: null, user_id: userId, spouse_id: null });
        }
        return next;
    }

    if (nativeName === 'French') {
        return [
            {
                ...createNativeLanguage('French', userId),
                ...native,
                language: 'French',
                level: NATIVE_LEVEL,
            },
            english
                ? { ...english, level: null, user_id: userId, spouse_id: null }
                : createProficiencyLanguage('English', userId),
        ];
    }

    const next: LanguageProficiency[] = [
        {
            ...createNativeLanguage(nativeName, userId),
            ...native,
            language: nativeName,
            level: NATIVE_LEVEL,
        },
        english
            ? { ...english, level: null, user_id: userId, spouse_id: null }
            : createProficiencyLanguage('English', userId),
    ];
    if (french) {
        next.push({ ...french, level: null, user_id: userId, spouse_id: null });
    }
    return next;
};

interface LanguageProficiencyStepProps {
    isLastStep: boolean;
    hasSpouse: string;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

interface LanguageFormValues {
    user_languages: LanguageProficiency[];
    spouse_languages: LanguageProficiency[];
    /** UI-only field for "Do you speak French?" (not sent to API). */
    speaks_french: boolean;
}

interface ProficiencySectionProps {
    title: string;
    fieldPrefix: string;
    language: LanguageProficiency;
    testOptions: ReturnType<typeof generateOptionsFromString>;
    setFieldValue: (field: string, value: unknown) => void;
}

const ProficiencySection = ({
    title,
    fieldPrefix,
    language,
    testOptions,
    setFieldValue,
}: ProficiencySectionProps) => {
    const languageScoreRange = getLanguageScoreRange(language.test_type || '');
    const step = getLanguageScoreStep(language.test_type || '');
    const disabled = !language.language || !language.test_type;

    const scoreLabelFormat = (value: number) =>
        formatLanguageScoreLabel(language.test_type || '', value);

    const handleTestTypeChange = (value: unknown) => {
        const nextRange = getLanguageScoreRange(String(value || ''));
        setFieldValue(`${fieldPrefix}.test_type`, value);
        setFieldValue(`${fieldPrefix}.listening`, nextRange.min);
        setFieldValue(`${fieldPrefix}.reading`, nextRange.min);
        setFieldValue(`${fieldPrefix}.speaking`, nextRange.min);
        setFieldValue(`${fieldPrefix}.writing`, nextRange.min);
    };

    return (
        <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1.5 }}>
                {title}
            </Typography>
            <StepNote messageKey="takenOfficialTest" sx={{ mb: 2 }} />
            <Divider sx={{ mb: 2 }} />
            <FormGridLayout>
                <FormField
                    type="autosuggest"
                    name={`${fieldPrefix}.test_type`}
                    label="testType"
                    options={testOptions}
                    disabled={!language.language}
                    onChange={handleTestTypeChange}
                />
                <Box />
                <FormikRangeSlider
                    name={`${fieldPrefix}.listening`}
                    label="listeningScore"
                    disabled={disabled}
                    min={languageScoreRange.min}
                    max={languageScoreRange.max}
                    step={step}
                    valueLabelFormat={scoreLabelFormat}
                />
                <FormikRangeSlider
                    name={`${fieldPrefix}.reading`}
                    label="readingScore"
                    disabled={disabled}
                    min={languageScoreRange.min}
                    max={languageScoreRange.max}
                    step={step}
                    valueLabelFormat={scoreLabelFormat}
                />
                <FormikRangeSlider
                    name={`${fieldPrefix}.speaking`}
                    label="speakingScore"
                    disabled={disabled}
                    min={languageScoreRange.min}
                    max={languageScoreRange.max}
                    step={step}
                    valueLabelFormat={scoreLabelFormat}
                />
                <FormikRangeSlider
                    name={`${fieldPrefix}.writing`}
                    label="writingScore"
                    disabled={disabled}
                    min={languageScoreRange.min}
                    max={languageScoreRange.max}
                    step={step}
                    valueLabelFormat={scoreLabelFormat}
                />
            </FormGridLayout>
        </Paper>
    );
};

const LanguageProficiencyStep = ({
    hasSpouse,
    onNext,
    onBack,
    isLastStep,
}: LanguageProficiencyStepProps) => {
    const user = useSelector((state: RootState) => state.user.entity);
    const userId = user?.id || 0;
    const { hasCanada } = useEnabledCountries();
    const showFrenchFeature = hasCanada;
    const partnerLanguageOptions = useMemo(
        () =>
            generateOptionsFromString(
                hasCanada ? Object.values(partnerLanguages) : [partnerLanguages.ENGLISH]
            ),
        [hasCanada]
    );
    const showSpouseTab = hasSpouse === 'Yes';

    const [activeTab, setActiveTab] = useState(0);
    const { data: userLanguages, isLoading: userLoading } = useLanguageProficiency();
    const { data: spouseLanguages, isLoading: spouseLoading } =
        useSpouseLanguageProficiency(showSpouseTab);
    const saveUserLanguages = useSaveLanguageProficiency();
    const saveSpouseLanguages = useSaveSpouseLanguageProficiency();

    const emptyLanguage = useMemo(() => createEmptyLanguage(userId), [userId]);

    const initialValues: LanguageFormValues = useMemo(() => {
        const normalized = normalizeUserLanguages(userLanguages, userId);
        const hasFrenchProficiency = normalized.some(
            (lang, index) => index > 0 && lang.language === 'French'
        );
        return {
            user_languages: normalized,
            spouse_languages: spouseLanguages && spouseLanguages.length > 0 ? spouseLanguages : [],
            speaks_french: hasFrenchProficiency,
        };
    }, [userLanguages, spouseLanguages, userId]);

    const handleSubmit = async (values: LanguageFormValues) => {
        try {
            const userLangsWithId = values.user_languages.map((lang) => ({
                ...lang,
                user_id: userId,
                spouse_id: null,
            }));
            await saveUserLanguages.mutateAsync(userLangsWithId);

            if (showSpouseTab && values.spouse_languages.length > 0) {
                const spouseLangsWithId = values.spouse_languages.map((lang) => ({
                    ...lang,
                    user_id: 0,
                    spouse_id: lang.spouse_id ?? null,
                }));
                await saveSpouseLanguages.mutateAsync(spouseLangsWithId);
            }

            onNext({
                user_languages: userLangsWithId,
                spouse_languages: showSpouseTab ? values.spouse_languages : [],
            });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (userLoading || (showSpouseTab && spouseLoading)) {
        return <FormSkeleton fields={6} />;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={languageSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, isValid, isSubmitting, setFieldValue, validateForm, setTouched }) => {
                    const nativeLanguage = values.user_languages[0]?.language || '';
                    const englishIdx = values.user_languages.findIndex(
                        (lang, index) => index > 0 && lang.language === 'English'
                    );
                    const frenchIdx = values.user_languages.findIndex(
                        (lang, index) => index > 0 && lang.language === 'French'
                    );

                    // Match LanguageFormV2 showEnglish / showFrench
                    const showEnglishSection =
                        Boolean(nativeLanguage) && nativeLanguage !== 'English' && englishIdx >= 0;
                    const showFrenchToggle =
                        showFrenchFeature && Boolean(nativeLanguage) && nativeLanguage !== 'French';
                    const showFrenchSection =
                        showFrenchToggle && values.speaks_french && frenchIdx >= 0;

                    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
                        void changeTabIfValid({
                            nextTab: newValue,
                            activeTab,
                            tabFieldKeys: [['user_languages'], ['spouse_languages']],
                            values,
                            validateForm,
                            setTouched,
                            setActiveTab,
                        });
                    };

                    const handleNativeLanguageChange = (value: unknown) => {
                        const nextNative = String(value || '');
                        const keepFrench =
                            values.speaks_french && nextNative !== 'French' && showFrenchFeature;

                        setFieldValue(
                            'user_languages',
                            buildLanguagesForNative(
                                nextNative,
                                values.user_languages,
                                userId,
                                keepFrench
                            )
                        );

                        if (nextNative === 'French') {
                            setFieldValue('speaks_french', false);
                        }
                    };

                    const handleSpeaksFrenchChange = (value: unknown) => {
                        const yes = value === true || value === 'true';
                        setFieldValue('speaks_french', yes);

                        if (yes && frenchIdx < 0) {
                            setFieldValue('user_languages', [
                                ...values.user_languages,
                                createProficiencyLanguage('French', userId),
                            ]);
                        } else if (!yes && frenchIdx >= 0) {
                            setFieldValue(
                                'user_languages',
                                values.user_languages.filter((_, index) => index !== frenchIdx)
                            );
                        }
                    };

                    const isSaving =
                        isSubmitting ||
                        saveUserLanguages.isPending ||
                        saveSpouseLanguages.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                {showSpouseTab && (
                                    <Tabs
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        sx={{ mb: 3 }}
                                    >
                                        <Tab label="Your Languages" />
                                        <Tab label="Spouse Languages" />
                                    </Tabs>
                                )}

                                {activeTab === 0 && (
                                    <Box>
                                        <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontSize: '1.1rem', mb: 2 }}
                                            >
                                                What is your native language?
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <FormGridLayout>
                                                <FormField
                                                    type="autosuggest"
                                                    name="user_languages.0.language"
                                                    label="nativeLanguage"
                                                    options={languageOptions}
                                                    onChange={handleNativeLanguageChange}
                                                />
                                            </FormGridLayout>
                                        </Paper>

                                        {showEnglishSection && (
                                            <ProficiencySection
                                                title="English Proficiency"
                                                fieldPrefix={`user_languages.${englishIdx}`}
                                                language={values.user_languages[englishIdx]}
                                                testOptions={englishTestOptions}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}

                                        {showFrenchToggle && (
                                            <Paper
                                                elevation={1}
                                                sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}
                                            >
                                                <FormField
                                                    type="radio"
                                                    row
                                                    name="speaks_french"
                                                    label="doYouSpeakFrench"
                                                    options={booleanOptions}
                                                    onChange={handleSpeaksFrenchChange}
                                                />
                                            </Paper>
                                        )}

                                        {showFrenchSection && (
                                            <ProficiencySection
                                                title="French Proficiency"
                                                fieldPrefix={`user_languages.${frenchIdx}`}
                                                language={values.user_languages[frenchIdx]}
                                                testOptions={frenchTestOptions}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                    </Box>
                                )}

                                {activeTab === 1 && showSpouseTab && (
                                    <FieldArray name="spouse_languages">
                                        {({ push, remove }) => (
                                            <Box>
                                                {values.spouse_languages.map(
                                                    (spouseLanguage, index) => (
                                                        <Box key={index}>
                                                            <Paper
                                                                elevation={1}
                                                                sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems: 'center',
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{ fontSize: '1.1rem' }}
                                                                    >
                                                                        Spouse Language {index + 1}
                                                                    </Typography>
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            remove(index)
                                                                        }
                                                                        color="error"
                                                                        size="small"
                                                                        aria-label="Remove spouse language"
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                </Box>
                                                                <Divider sx={{ mb: 2 }} />
                                                                <FormGridLayout>
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`spouse_languages.${index}.language`}
                                                                        label="language"
                                                                        options={
                                                                            partnerLanguageOptions
                                                                        }
                                                                        onChange={(value) => {
                                                                            // PartnerLanguageFormV2: changing language resets test + scores
                                                                            setFieldValue(
                                                                                `spouse_languages.${index}.language`,
                                                                                value
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_languages.${index}.test_type`,
                                                                                ''
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_languages.${index}.listening`,
                                                                                0
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_languages.${index}.reading`,
                                                                                0
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_languages.${index}.speaking`,
                                                                                0
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_languages.${index}.writing`,
                                                                                0
                                                                            );
                                                                        }}
                                                                    />
                                                                </FormGridLayout>
                                                            </Paper>

                                                            {spouseLanguage.language ? (
                                                                <ProficiencySection
                                                                    title={`${spouseLanguage.language} Proficiency`}
                                                                    fieldPrefix={`spouse_languages.${index}`}
                                                                    language={spouseLanguage}
                                                                    testOptions={
                                                                        spouseLanguage.language ===
                                                                        'French'
                                                                            ? frenchTestOptions
                                                                            : englishTestOptions
                                                                    }
                                                                    setFieldValue={setFieldValue}
                                                                />
                                                            ) : null}
                                                        </Box>
                                                    )
                                                )}

                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Add />}
                                                    onClick={() =>
                                                        push({
                                                            ...emptyLanguage,
                                                            spouse_id: null,
                                                        })
                                                    }
                                                    fullWidth
                                                    sx={{ mb: 3 }}
                                                >
                                                    Add Spouse Language
                                                </Button>
                                            </Box>
                                        )}
                                    </FieldArray>
                                )}

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default LanguageProficiencyStep;
